import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import PendingApprovalModal from '../PendingApprovalModal/PendingApprovalModal';
import './AuthPage.css';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true); // TD3-129: Registration re-enabled with admin approval
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nickname: ''
  });
  // TD3-151: Terms of Service and 18+ age acknowledgement
  const [tosAccepted, setTosAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    hasSpecial: false,
    hasUpper: false,
    hasNumber: false,
    score: 0
  });
  
  const {
    user,
    login,
    register,
    showLoginTransition,
    // TD3-129: Email verification modal state (Gate 1)
    showEmailVerificationModal,
    setShowEmailVerificationModal,
    pendingVerificationEmail,
    requestVerification,
    // TD3-129: Admin approval modal state (Gate 2)
    showPendingApprovalModal,
    setShowPendingApprovalModal,
    pendingApprovalEmail
  } = useAuth();
  // FUTURE FEATURE: Add requestPasswordReset when password reset is enabled
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  // TD3-105: Wait for login transition to complete before navigating
  useEffect(() => {
    if (user && !showLoginTransition) {
      navigate('/dashboard');
    }
  }, [user, showLoginTransition, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePasswordStrength = (password) => {
    const length = password.length >= 8;
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    let score = 0;
    if (length) score += 1;
    if (hasSpecial) score += 1;
    if (hasUpper) score += 1;
    if (hasNumber) score += 1;
    
    return { length, hasSpecial, hasUpper, hasNumber, score };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Real-time password validation for registration
    if (name === 'password' && !isLogin) {
      setPasswordStrength(validatePasswordStrength(value));
    }
    
    setError(''); // Clear error when user types
    setSuccess(''); // Clear success message when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isForgotPassword) {
        // Password reset request
        if (!validateEmail(formData.email)) {
          setError('Por favor, insira um email válido');
          setLoading(false);
          return;
        }

        const result = await requestPasswordReset(formData.email);
        if (result.success) {
          setSuccess('Email de recuperação enviado! Verifique sua caixa de entrada.');
          setFormData({ email: '', password: '', confirmPassword: '', nickname: '' });
        } else {
          setError(result.error);
        }
      } else if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          // TD3-105: Navigation happens in useEffect after transition completes
          // Don't navigate here - let the transition run first
        } else {
          setError(result.error);
        }
      } else {
        // Registration validation
        if (!validateEmail(formData.email)) {
          setError('Por favor, insira um email válido (ex: usuario@exemplo.com)');
          setLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError('Senhas não coincidem');
          setLoading(false);
          return;
        }

        if (formData.password.length < 8) {
          setError('Senha deve ter pelo menos 8 caracteres');
          setLoading(false);
          return;
        }

        // TD3-151: Validate ToS acceptance
        if (!tosAccepted) {
          setError('Você deve aceitar os Termos de Serviço e confirmar ter 18 anos ou mais');
          setLoading(false);
          return;
        }

        const result = await register(formData.email, formData.password, formData.confirmPassword, formData.nickname, tosAccepted);
        if (result.success) {
          // TD3-129: Show success message (user stays on auth page, must verify email)
          setSuccess(result.message || 'Conta criada com sucesso! Verifique seu e-mail.');
          setFormData({ email: '', password: '', confirmPassword: '', nickname: '' });
          // TD3-151: Reset ToS acceptance after successful registration
          setTosAccepted(false);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      nickname: ''
    });
    // TD3-151: Reset ToS acceptance when switching modes
    setTosAccepted(false);
  };

  const toggleForgotPassword = () => {
    setIsForgotPassword(!isForgotPassword);
    setIsLogin(false);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      nickname: ''
    });
  };

  return (
    <div className="auth-container">
      {/* Mobile warning for screens below 1200px */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main auth form for laptop screens */}
      <div className="auth-form-container">
        <div className="auth-header">
          <h1>Bem-vindo ao Oráculo dos 12 Raios</h1>
          <p>Descubra os segredos que o universo tem para você</p>
        </div>

        <div className="auth-form">
          {!isForgotPassword && (
            <div className="auth-tabs">
              <button
                className={`tab ${isLogin ? 'active' : ''}`}
                type="button"
                onClick={() => setIsLogin(true)}
              >
                Entrar
              </button>
              <button
                className={`tab ${!isLogin ? 'active' : ''}`}
                type="button"
                onClick={() => setIsLogin(false)}
              >
                Criar Conta
              </button>
            </div>
          )}

          {isForgotPassword && (
            <div className="forgot-password-header">
              <h3>Recuperar Senha</h3>
              <p>Digite seu email para receber instruções de recuperação</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="form">
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="seu@email.com"
                autoComplete="email"
              />
            </div>

            {!isLogin && !isForgotPassword && (
              <div className="input-group">
                <label htmlFor="nickname">Como gostaria de ser chamado?</label>
                <input
                  type="text"
                  id="nickname"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleInputChange}
                  placeholder="Nome ou apelido (opcional)"
                  maxLength={50}
                  autoComplete="off"
                />
              </div>
            )}

            {!isForgotPassword && (
              <div className="input-group">
                <label htmlFor="password">Senha</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Sua senha"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
              </div>
            )}
              
              {!isLogin && !isForgotPassword && formData.password && (
                <div className="password-requirements">
                  <div className="requirement-header">
                    <span>Requisitos da senha:</span>
                    {passwordStrength.score === 4 && (
                      <span className="strength-excellent">✨ Excelente segurança!</span>
                    )}
                    {passwordStrength.score === 3 && (
                      <span className="strength-good">👍 Boa segurança</span>
                    )}
                    {passwordStrength.score < 3 && passwordStrength.length && (
                      <span className="strength-basic">Mínimo atingido</span>
                    )}
                  </div>
                  <ul className="requirements-list">
                    <li className={passwordStrength.length ? 'met' : 'unmet'}>
                      {passwordStrength.length ? '✓' : '○'} Mínimo 8 caracteres
                    </li>
                    <li className={passwordStrength.hasUpper ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasUpper ? '✓' : '○'} Uma letra maiúscula (recomendado)
                    </li>
                    <li className={passwordStrength.hasNumber ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasNumber ? '✓' : '○'} Um número (recomendado)
                    </li>
                    <li className={passwordStrength.hasSpecial ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasSpecial ? '✓' : '○'} Um caractere especial (recomendado)
                    </li>
                  </ul>
                </div>
              )}

            {!isLogin && !isForgotPassword && (
              <div className="input-group">
                <label htmlFor="confirmPassword">Confirmar Senha</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirme sua senha"
                  autoComplete="new-password"
                />
              </div>
            )}

            {/* TD3-151: Terms of Service and 18+ age acknowledgement checkbox */}
            {!isLogin && !isForgotPassword && (
              <div className="tos-checkbox-group">
                <label className="tos-checkbox-label">
                  <input
                    type="checkbox"
                    checked={tosAccepted}
                    onChange={(e) => setTosAccepted(e.target.checked)}
                    className="tos-checkbox"
                  />
                  <span className="tos-text">
                    Li e aceito os{' '}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tos-link"
                    >
                      Termos de Serviço
                    </a>
                    {' '}e confirmo ter 18 anos ou mais
                  </span>
                </label>
              </div>
            )}

            {/* TD3-151: Error/success messages positioned near submit button */}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit" 
              className="submit-btn primary-cta"
              disabled={loading || (!isLogin && !isForgotPassword && !passwordStrength.length)}
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                isForgotPassword ? 'Enviar Email de Recuperação' : 
                isLogin ? 'Entrar' : 'Criar Conta'
              )}
            </button>
          </form>

          <div className="auth-footer">
            {!isForgotPassword ? (
              <>
                {!isLogin && (
                  <p style={{textAlign: 'center', color: '#666', fontSize: '0.9rem'}}>
                    ℹ️ Período Beta: Contas são aprovadas manualmente<br/>
                    Você receberá um e-mail quando sua conta for aprovada
                  </p>
                )}
                {/* FUTURE FEATURE: Password reset functionality - commented out for Phase 1 MVP */}
                {/* {isLogin && (
                  <p>
                    <button
                      type="button"
                      className="link-button forgot-password-link"
                      onClick={toggleForgotPassword}
                    >
                      Esqueceu sua senha?
                    </button>
                  </p>
                )} */}
              </>
            ) : (
              <p>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setIsLogin(true);
                    setError('');
                    setSuccess('');
                  }}
                >
                  ← Voltar ao login
                </button>
              </p>
            )}
          </div>

          {/* Future payment integration placeholder */}
          <div className="payment-placeholder">
            <p>💳 Integração de pagamento será implementada na próxima fase</p>
          </div>
        </div>
      </div>

      {/* TD3-129: Email Verification Modal (Gate 1) */}
      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={() => setShowEmailVerificationModal(false)}
        userEmail={pendingVerificationEmail}
        onResendVerification={requestVerification}
      />

      {/* TD3-129: Pending Approval Modal (Gate 2) */}
      <PendingApprovalModal
        isOpen={showPendingApprovalModal}
        onClose={() => setShowPendingApprovalModal(false)}
        userEmail={pendingApprovalEmail}
      />
    </div>
  );
};

export default AuthPage;